/**
 * React Query Hook: Delete Branch File
 *
 * Marks a file as deleted in the user's working set (soft-delete branch,
 * status=DELETED). Creates a delete-branch over the current main content if
 * none exists — mirrors BranchContext.delete_file on the backend.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { DELETE_BRANCH_FILE_MUTATION } from '@/services/metadata_v1/queries';
import type {
  DeleteBranchFileResponse,
  DeleteBranchFileVariables,
  MetadataBranch,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

async function deleteBranchFile(
  variables: DeleteBranchFileVariables
): Promise<MetadataBranch> {
  const response = await graphqlRequest<DeleteBranchFileResponse>(
    DELETE_BRANCH_FILE_MUTATION,
    variables
  );

  if (!response?.deleteBranchFile) {
    throw new Error('Delete failed. Please try again.');
  }

  return response.deleteBranchFile;
}

export interface UseDeleteBranchFileInput {
  metadataItemId: string;
}

export interface UseDeleteBranchFileOptions {
  onSuccess?: (data: MetadataBranch) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to mark a file as deleted in the user's working set.
 *
 * Invalidates `activeBranch` and `userBranches` caches on success — mirrors
 * useSaveBranchFile's invalidation so a subscriber watching either the tab id
 * or the metadataItem id sees the DELETED status.
 */
export function useDeleteBranchFile(
  options?: UseDeleteBranchFileOptions
): UseMutationResult<MetadataBranch, Error, UseDeleteBranchFileInput> {
  const queryClient = useQueryClient();
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const organizationId = selectedOrg?.id;

  return useMutation({
    mutationFn: ({ metadataItemId }: UseDeleteBranchFileInput) =>
      deleteBranchFile({ input: { metadataItemId } }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['activeBranch'] });

      queryClient.invalidateQueries({
        queryKey: ['userBranches', organizationId],
      });

      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
